import React, { useCallback } from "react";
import Container from "react-bootstrap/esm/Container";
import Navbar from "react-bootstrap/esm/Navbar";
import { TalkWithUsPrompt } from "./talk_with_us_prompt";

import "./offering_header.css";
import { LocaleOption } from "../../offering/locale_data";
import { Locale, useI18n } from "../../lib/i18n/i18n_provider";
import { LocalePicker } from "./locale_picker";
import { useUTMQueryString } from "../../hooks";
import { useAnalytics } from "../../lib/analytics/analytics";
import { OfferingMessageType } from "@/offering/api";

interface OfferingHeaderProps {
  logo?: string;
  homepageURL?: string;
  offeringID: string;
  showTalkWithUsPrompt?: boolean;
  talkWithUsMessage?: string;
  hubspotForm?: React.ReactNode;
  messageableType?: OfferingMessageType;
  partnerID: string;
  localeOptions?: LocaleOption[];
  onLocaleSwitch?: () => void;
  hubspotForms?: {
    [key: string]: React.ReactNode;
  };
}

export function OfferingHeader(props: OfferingHeaderProps) {
  const {
    logo,
    homepageURL,
    offeringID,
    hubspotForm,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    messageableType = OfferingMessageType.PartnerOffering,
    partnerID,
    localeOptions,
    onLocaleSwitch,
    hubspotForms,
  } = props;

  const { locale, onChangeLocale } = useI18n();

  const utmQuery = useUTMQueryString();
  const homepageURLWithUTMParams = `${homepageURL}${utmQuery}`;

  const onLocalePickerSelect = React.useCallback(
    (locale: Locale) => {
      onChangeLocale(locale);
      if (onLocaleSwitch) onLocaleSwitch();
    },
    [onChangeLocale, onLocaleSwitch]
  );

  const analytics = useAnalytics();

  const onLogoClick = useCallback(() => {
    analytics.event(
      "Logo Click",
      {
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics]);

  return (
    <>
      <Navbar className="bg-light fixed-top offering-header">
        <Container>
          <Navbar.Brand
            href={homepageURLWithUTMParams}
            className="offering-header-logo-link"
            onMouseDown={onLogoClick}
          >
            <img className="offering-header__logo" alt="logo" src={logo} />
          </Navbar.Brand>
          <Navbar.Collapse className="justify-content-end">
            {localeOptions && localeOptions.length > 0 && (
              <Navbar.Text>
                <LocalePicker
                  localeOptions={localeOptions}
                  locale={locale}
                  onChangeLocale={onLocalePickerSelect}
                />
              </Navbar.Text>
            )}
            {showTalkWithUsPrompt ? (
              <Navbar.Text className="fw-bold d-none d-md-block">
                <TalkWithUsPrompt
                  partnerID={partnerID}
                  hubspotForm={hubspotForm}
                  messageableID={offeringID}
                  messageableType={messageableType}
                  hubspotForms={hubspotForms}
                />
              </Navbar.Text>
            ) : (
              talkWithUsMessage && (
                <Navbar.Text className="fw-bold d-none d-md-block">
                  {talkWithUsMessage}
                </Navbar.Text>
              )
            )}
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <div className="offering-header__spacer"></div>
    </>
  );
}
